/**
 * Browser push notifications.
 *
 * A friend request that arrives while the tab is closed only reaches the player
 * if the operating system shows it, and that needs three things to line up: a
 * registered service worker (sw.js), the user's permission, and a subscription
 * the server can send to. This module owns all three and reports one state, so
 * the Settings switch can say what is actually true rather than what was asked
 * for.
 *
 * The subscription belongs to an account, not a browser: it is re-posted on
 * every boot so that signing in on a shared machine moves the notifications with
 * the account instead of leaving them with the previous one.
 */

import { getToken, isLoggedIn } from "./account.js";

export type PushState =
  /** no service worker / no Push API — an iPhone that has not been added to the home screen, mostly */
  | "unsupported"
  /** the user said no; the browser will not ask again from a click */
  | "blocked"
  /** permission granted and a subscription is registered with the server */
  | "on"
  /** available, not switched on */
  | "off";

const SW_URL = "/sw.js";

export function pushSupported(): boolean {
  return typeof navigator !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window;
}

function authHeader(): Record<string, string> {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/** The VAPID application server key, base64url, as the server generated it. */
async function serverKey(): Promise<Uint8Array | null> {
  try {
    const res = await fetch("/api/push/key");
    if (!res.ok) return null;
    const { key } = await res.json();
    return typeof key === "string" ? decodeBase64Url(key) : null;
  } catch {
    return null;
  }
}

/** base64url -> bytes. `applicationServerKey` will not take the string form. */
function decodeBase64Url(s: string): Uint8Array {
  const b64 = (s + "=".repeat((4 - (s.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

let registration: ServiceWorkerRegistration | null = null;
async function ensureWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!pushSupported()) return null;
  if (registration) return registration;
  try {
    registration = await navigator.serviceWorker.register(SW_URL, { scope: "/" });
    // A brand-new registration has no active worker yet, and subscribing needs
    // one — `ready` is the only honest way to wait for it.
    await navigator.serviceWorker.ready;
    return registration;
  } catch {
    return null;
  }
}

/** What the switch should show right now. */
export async function pushState(): Promise<PushState> {
  if (!pushSupported()) return "unsupported";
  if (Notification.permission === "denied") return "blocked";
  if (Notification.permission !== "granted") return "off";
  try {
    const reg = await navigator.serviceWorker.getRegistration(SW_URL);
    const sub = await reg?.pushManager.getSubscription();
    return sub ? "on" : "off";
  } catch {
    return "off";
  }
}

async function postSubscription(sub: PushSubscription): Promise<boolean> {
  try {
    const res = await fetch("/api/push/subscribe", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeader() },
      body: JSON.stringify({ subscription: sub.toJSON() }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Turn push on. Must be called from a user gesture: `requestPermission` is
 * ignored (or auto-denied) otherwise.
 */
export async function enablePush(): Promise<{ ok: boolean; state: PushState; error?: string }> {
  if (!pushSupported()) return { ok: false, state: "unsupported" };
  if (!isLoggedIn()) return { ok: false, state: "off", error: "signin" };

  const permission = Notification.permission === "granted"
    ? "granted"
    : await Notification.requestPermission();
  if (permission !== "granted") return { ok: false, state: permission === "denied" ? "blocked" : "off" };

  const reg = await ensureWorker();
  if (!reg) return { ok: false, state: "off", error: "worker" };

  const key = await serverKey();
  if (!key) return { ok: false, state: "off", error: "server" };

  try {
    // An existing subscription may have been made against a different server
    // key (a redeploy that lost vapid.json): it would be accepted here and then
    // fail on every send, so it is replaced rather than reused.
    const existing = await reg.pushManager.getSubscription();
    const sub = existing ?? await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: key as BufferSource,
    });
    const ok = await postSubscription(sub);
    return ok ? { ok: true, state: "on" } : { ok: false, state: "off", error: "server" };
  } catch {
    return { ok: false, state: "off", error: "subscribe" };
  }
}

/** Turn push off: drop the browser subscription and forget it server-side. */
export async function disablePush(): Promise<PushState> {
  try {
    const reg = await navigator.serviceWorker.getRegistration(SW_URL);
    const sub = await reg?.pushManager.getSubscription();
    if (sub) {
      await fetch("/api/push/unsubscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({ endpoint: sub.endpoint }),
      }).catch(() => { /* the browser end still comes off */ });
      await sub.unsubscribe();
    }
  } catch {
    /* nothing to undo */
  }
  return "off";
}

/**
 * Re-attach an existing subscription to whoever is signed in now. Called on
 * boot: it costs one request, and without it a subscription made by the
 * previous account on this browser would keep receiving that account's
 * notifications.
 */
export async function syncPushSubscription(): Promise<void> {
  if (!pushSupported() || !isLoggedIn() || Notification.permission !== "granted") return;
  try {
    const reg = await navigator.serviceWorker.getRegistration(SW_URL);
    const sub = await reg?.pushManager.getSubscription();
    if (sub) await postSubscription(sub);
  } catch {
    /* best effort */
  }
}
